/**
 * Progressive presentation of the Kraken before and between breaches.
 *
 * This is kept engine-free so benchmarks and tests can prove the range grammar without
 * booting Babylon. The visible mesh is deliberately withheld at long range: the player
 * first reads displaced water and an uncertain mass, then partial limbs, and only sees
 * the surfaced animal inside the final few kilometres.
 */

export interface KrakenPresence {
  /** 0 at the first 10 km hint, 1 beside the rig. */
  clarity: number;
  /** Visibility multiplier for the fully modelled surfaced animal. */
  bodyVisibility: number;
  /** Opacity multiplier for the broad soft-edged underwater mass. */
  shadowAlpha: number;
  /** Horizontal growth of the implied submerged body as it becomes legible. */
  shadowScale: number;
  /** Metres of broad surface lift at the centre of the pressure mass. */
  bulgeHeight: number;
  /** Strength of broken pressure foam around the submerged shape. */
  wakeAlpha: number;
  /** How much of the separate tentacle shadows may be inferred. */
  armVisibility: number;
  /**
   * How much the underwater mass reads as a shadow rather than as a pale stain, 0..1.
   *
   * At distance the shape has to be *lighter* than the sea to be seen at all — a dark
   * smudge against dark water at 5 km is nothing, and the pale treatment is what makes the
   * long-range approach work. Up close that same treatment is a bright plate lying on the
   * surface, which is the opposite of a thing underneath it. This ramps over the last two
   * kilometres and nowhere else, so the far and middle distances are untouched.
   */
  shadowDarkness: number;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

/**
 * The approach starts as an uncertain kilometre-wide stain at 10 km. A non-submerged
 * boss cross-fades into its body between 2.6 and 1.85 km.
 *
 * `submergence` is how far through its dive the body is, 0 up and 1 under — a dive is a
 * movement, not a switch, so the body fades as it goes rather than being deleted on the
 * frame the phase changes. Callers that only have the boolean can pass it: `true` is 1.
 */
export function krakenPresence(
  rangeMetres: number,
  submerged: boolean | number,
): KrakenPresence {
  const range = Math.max(0, rangeMetres);
  const submergence = clamp01(typeof submerged === 'number' ? submerged : (submerged ? 1 : 0));
  const inSensorEnvelope = 1 - smoothstep((range - 10_000) / 2_000);
  const clarity = smoothstep((10_000 - range) / 8_000);
  /*
   * Depth dims the body rather than deleting it.
   *
   * The submerged animal used to be replaced wholesale by a painted surface overlay,
   * which Adam struck ("remove ALL these rings, no matter the size"). What stands in its
   * place is the real mesh going down through the surface — so for almost all of the
   * dive the body must stay SOLID, because the sea is what hides it. A fade proportional
   * to depth made the descending mantle translucent and the horizon showed through it: a
   * ghost going down rather than a mass. This holds full opacity until three quarters
   * under and only then lets go, by which point the surface has it anyway.
   */
  const depthFade = 1 - smoothstep((submergence - 0.75) / 0.25);
  const surfaceBlend = smoothstep((2_600 - range) / 750) * depthFade;
  const shadowAlpha = (
    submergence > 0
      ? 0.76 * submergence
      : (0.52 + clarity * 0.20) * (1 - surfaceBlend * 0.82)
  ) * inSensorEnvelope;

  return {
    clarity,
    bodyVisibility: surfaceBlend,
    shadowAlpha,
    shadowScale: 0.76 + clarity * 0.30,
    /*
     * The submerged dome grows as it closes. The dread note is "a bulge circles", and at
     * a kilometre a three-metre bulge under a six-hundred-metre body is a rounding error
     * on the swell — the close read *is* the dome, a hump of displaced water the eye
     * finds because the sea is wrong, not because a shape was painted on it. Surfaced,
     * the body itself takes over and the bulge stays what it always was.
     */
    bulgeHeight: (0.42 + clarity * 2.45)
      * (1 + submergence * (0.18 + smoothstep((3_000 - range) / 2_000) * 1.9)),
    wakeAlpha: (0.28 + clarity * 0.46) * inSensorEnvelope,
    armVisibility: submergence > 0
      ? 0.42 + clarity * 0.58
      : smoothstep((clarity - 0.24) / 0.68),
    // Nothing until 3 km, fully dark by 1 km — the band the dread phase happens in.
    shadowDarkness: smoothstep((3_000 - range) / 2_000),
  };
}

